import { IAssessmentAugmantin, IAssessmentSchemaInfo, IAssessmentsQuestionItemSchema, ILCHttpResponse } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export interface IAssessmentTopicWithType {
  topicId: string
  topicName: string
  topicType: 'core' | 'roleBased'
  /** UI category from AppConfig defaults.topicGroups (stamped by gateway). */
  topicGroup?: string
}

export interface IAssessmentQuestions {
  [questionId: string]: IAssessmentsQuestionItemSchema
}

// Result of a delete request. The backend decides whether the assessment was
// permanently removed ('deleted') or kept and moved to the archive ('archived').
export interface IDeleteCustomAssessmentResult {
  action: 'deleted' | 'archived'
}

// One active custom assessment row for the Settings view tab list.
export interface IActiveCustomAssessment {
  info: IAssessmentSchemaInfo
  topicCount: number
  inUse: boolean
}

// One archived custom assessment row for the Settings archive view.
// `inUse` controls whether the delete action is enabled in the UI.
export interface IArchivedCustomAssessment {
  info: IAssessmentSchemaInfo
  topicCount: number
  archivedAt?: string
  inUse: boolean
}

export const assessmentsApi = createApi({
  reducerPath: 'assessmentsApi',
  // Tags keep dropdowns, active list, and archived tab in sync after create/delete/archive.
  tagTypes: ['AssessmentsInfo', 'CustomAssessments'],
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/assessments`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    getAssessmentsInfo: builder.query<IAssessmentSchemaInfo[], void>({
      query: () => ({
        url: 'info',
      }),
      providesTags: ['AssessmentsInfo'],
      transformResponse: (rawResult: ILCHttpResponse<IAssessmentSchemaInfo[]>, meta) => {
        return rawResult.data as IAssessmentSchemaInfo[]
      },
    }),
    getActiveTopicIds: builder.query<string[], void>({
      query: () => ({ url: 'active-topic-ids' }),
      transformResponse: (rawResult: ILCHttpResponse<string[]>) => rawResult.data as string[],
    }),
    getAssessmentsTopicsWithType: builder.query<IAssessmentTopicWithType[], string>({
      query: (language) => ({
        url: `topics/${language}`,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IAssessmentTopicWithType[]>, meta) => {
        return rawResult.data as IAssessmentTopicWithType[]
      },
    }),
    getAssessmentsQuestions: builder.query<IAssessmentQuestions, string>({
      query: (language) => ({
        url: `questions/${language}`,
      }),
      transformResponse: (rawResult: ILCHttpResponse<IAssessmentQuestions>, meta) => {
        return rawResult.data as IAssessmentQuestions
      },
    }),
    getAssessmentAugmantin: builder.query<IAssessmentAugmantin, { assessmentId: number; language: string }>({
      query: ({ assessmentId, language }) => ({
        url: `${assessmentId}/${language}`,
      }),
      // Same tag as custom list mutations so topic edits refresh the View preview.
      providesTags: ['CustomAssessments'],
      transformResponse: (rawResult: ILCHttpResponse<IAssessmentAugmantin>) => {
        return rawResult.data as IAssessmentAugmantin
      },
    }),
    createCustomAssessment: builder.mutation<IAssessmentAugmantin, any>({
      query: (assessmentToCreate: any) => ({
        url: `custom`,
        method: 'POST',
        body: assessmentToCreate,
      }),
      invalidatesTags: ['AssessmentsInfo', 'CustomAssessments'],
    }),
    // Edit the topics of an active custom assessment. The backend only allows this
    // for active assessments that are NOT in use, to protect historical evaluations.
    updateCustomAssessment: builder.mutation<IAssessmentAugmantin, { assessmentId: number; topics: string[] }>({
      query: ({ assessmentId, topics }) => ({
        url: `custom/${assessmentId}`,
        method: 'PATCH',
        body: { topics },
      }),
      invalidatesTags: ['AssessmentsInfo', 'CustomAssessments'],
      transformResponse: (rawResult: ILCHttpResponse<IAssessmentAugmantin>) => {
        return rawResult.data as IAssessmentAugmantin
      },
    }),
    // Delete a custom assessment. Backend hard deletes when not in use, or archives
    // when an active assessment is still referenced by evaluations.
    deleteCustomAssessment: builder.mutation<IDeleteCustomAssessmentResult, number>({
      query: (assessmentId: number) => ({
        url: `custom/${assessmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AssessmentsInfo', 'CustomAssessments'],
      transformResponse: (rawResult: ILCHttpResponse<IDeleteCustomAssessmentResult>) => {
        return rawResult.data as IDeleteCustomAssessmentResult
      },
    }),
    // List active custom assessments for the Settings view tab (includes in-use flag).
    getActiveCustomAssessments: builder.query<IActiveCustomAssessment[], void>({
      query: () => ({ url: 'custom/active' }),
      providesTags: ['CustomAssessments'],
      transformResponse: (rawResult: ILCHttpResponse<IActiveCustomAssessment[]>) => {
        return rawResult.data as IActiveCustomAssessment[]
      },
    }),
    // List archived custom assessments for the Settings archive view.
    getArchivedCustomAssessments: builder.query<IArchivedCustomAssessment[], void>({
      query: () => ({ url: 'custom/archived' }),
      providesTags: ['CustomAssessments'],
      transformResponse: (rawResult: ILCHttpResponse<IArchivedCustomAssessment[]>) => {
        return rawResult.data as IArchivedCustomAssessment[]
      },
    }),
  }),
})

export const {
  useCreateCustomAssessmentMutation,
  useUpdateCustomAssessmentMutation,
  useDeleteCustomAssessmentMutation,
  useGetActiveCustomAssessmentsQuery,
  useGetArchivedCustomAssessmentsQuery,
  useLazyGetAssessmentsInfoQuery,
  useLazyGetAssessmentsQuestionsQuery,
  useLazyGetAssessmentAugmantinQuery,
  useLazyGetAssessmentsTopicsWithTypeQuery,
  useLazyGetActiveTopicIdsQuery,
} = assessmentsApi
